import React, { useState, useEffect } from "react";
import { Plus, X, Loader2, Trash2, Users, Edit, Building2, User, Phone, FileText, MapPin, ChevronDown, ChevronUp, UserX, UserCheck, Warehouse, UserPlus, KeyRound, Save, RefreshCw, FileDown, Mail, History, ListPlus } from "lucide-react";
import CustomPriceListModal from "./CustomPriceListModal";
import { toast } from "sonner";
import axios from "axios";
import { useAdminData, useAdminCrud, authHeaders } from "@/hooks/useAdminData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  email: "", password: "", name: "", company: "",
  partita_iva: "", codice_fiscale: "", codice_sdi: "", pec: "",
  indirizzo: "", citta: "", cap: "", provincia: "",
  referente_nome: "", referente_telefono: "", referente_ruolo: "",
  telefono_azienda: "", note: "", warehouse_enabled: false,
};

function FormField({ label, field, type = "text", placeholder = "", half = false, testid = "", form, setForm }) {
  return (
    <div className={half ? "" : "col-span-2"}>
      <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">{label}</label>
      <input type={type} value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        className="w-full h-9 px-3 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none"
        placeholder={placeholder} data-testid={testid || `client-${field}`} />
    </div>
  );
}

export default function AdminClients() {
  const { data: clients, reload } = useAdminData("clients");
  const { create, update, remove } = useAdminCrud("clients");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [expandedRow, setExpandedRow] = useState(null);

  const openCreate = () => { setEditId(null); setForm({ ...emptyForm }); setShowForm(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      email: c.email || "", password: "", name: c.name || "", company: c.company || "",
      partita_iva: c.partita_iva || "", codice_fiscale: c.codice_fiscale || "",
      codice_sdi: c.codice_sdi || "", pec: c.pec || "",
      indirizzo: c.indirizzo || "", citta: c.citta || "", cap: c.cap || "", provincia: c.provincia || "",
      referente_nome: c.referente_nome || "", referente_telefono: c.referente_telefono || "",
      referente_ruolo: c.referente_ruolo || "",
      telefono_azienda: c.telefono_azienda || "", note: c.note || "",
      warehouse_enabled: c.warehouse_enabled || false,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) { toast.error("Nome e email sono obbligatori"); return; }
    if (!editId && !form.password) { toast.error("La password e obbligatoria per un nuovo cliente"); return; }
    setSaving(true);
    try {
      if (editId) {
        await update(editId, form);
        toast.success("Cliente aggiornato");
      } else {
        await create(form);
        toast.success("Cliente creato con successo!");
      }
      setShowForm(false);
      setForm({ ...emptyForm });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const deleteClient = async (id, name) => {
    if (!window.confirm(`Eliminare il cliente ${name}?`)) return;
    try {
      await remove(id);
      toast.success("Cliente eliminato");
      reload();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const toggleActive = async (e, client) => {
    e.stopPropagation();
    const action = client.is_active === false ? "reactivate" : "deactivate";
    const label = action === "deactivate" ? "Disattivare" : "Riattivare";
    if (!window.confirm(`${label} il cliente ${client.company || client.name}?`)) return;
    try {
      const res = await axios.post(`${API}/admin/data-governance/client/${client.id}/${action}`, {}, authHeaders());
      toast.success(res.data.message);
      reload();
    } catch { toast.error("Errore nell'operazione"); }
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-500">{clients.length} clienti registrati</p>
        <button onClick={openCreate}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-4 py-2 text-sm font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors"
          data-testid="create-client-btn">
          <Plus size={16} /> Nuovo Cliente
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-left table-mobile-cards" data-testid="clients-table">
          <thead className="border-b border-zinc-200 bg-zinc-50">
            <tr>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Codice</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Azienda / Nome</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">P.IVA</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Email / PEC</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Referente</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading">Citta</th>
              <th className="px-4 py-3 text-xs font-bold tracking-widest uppercase text-zinc-500 font-heading text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {clients.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center">
                <Users size={40} className="text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">Nessun cliente registrato</p>
              </td></tr>
            ) : (
              clients.map((c) => (
                <React.Fragment key={c.id}>
                  <tr className={`hover:bg-zinc-50 cursor-pointer ${c.is_active === false ? "opacity-50 bg-zinc-50" : ""}`} onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)} data-testid={`client-row-${c.id}`}>
                    <td className="px-4 py-3" data-label="Codice">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-[#FF5200]/10 text-[#FF5200] text-xs font-bold font-mono rounded-sm">{c.client_code || "-"}</span>
                        {c.is_active === false && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold uppercase tracking-widest rounded-sm" data-testid={`client-inactive-badge-${c.id}`}>Inattivo</span>}
                        {c.warehouse_enabled && <span className="px-1.5 py-0.5 bg-violet-100 text-violet-600 text-[10px] font-bold uppercase tracking-widest rounded-sm">Magazzino</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-label="Azienda">
                      <p className="text-sm font-bold text-[#09090B]">{c.company || c.name}</p>
                      {c.company && <p className="text-xs text-zinc-500">{c.name}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600 font-mono" data-label="P.IVA">{c.partita_iva || "-"}</td>
                    <td className="px-4 py-3" data-label="Email">
                      <p className="text-sm text-zinc-600">{c.email}</p>
                      {c.pec && <p className="text-xs text-zinc-400">PEC: {c.pec}</p>}
                    </td>
                    <td className="px-4 py-3" data-label="Referente">
                      {c.referente_nome ? (
                        <div>
                          <p className="text-sm text-zinc-700">{c.referente_nome}</p>
                          {c.referente_ruolo && <p className="text-xs text-zinc-400">{c.referente_ruolo}</p>}
                        </div>
                      ) : <span className="text-sm text-zinc-400">-</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600" data-label="Citta">{c.citta || "-"}</td>
                    <td className="px-4 py-3 text-right actions-cell no-label">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="p-1.5 text-zinc-400 hover:text-[#FF5200]" title="Modifica" data-testid={`edit-client-${c.id}`}><Edit size={14} /></button>
                        {c.is_active === false ? (
                          <button onClick={(e) => toggleActive(e, c)} className="p-1.5 text-green-400 hover:text-green-600" title="Riattiva" data-testid={`reactivate-client-${c.id}`}><UserCheck size={14} /></button>
                        ) : (
                          <button onClick={(e) => toggleActive(e, c)} className="p-1.5 text-zinc-400 hover:text-amber-500" title="Disattiva" data-testid={`deactivate-client-${c.id}`}><UserX size={14} /></button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); deleteClient(c.id, c.name); }} className="p-1.5 text-zinc-400 hover:text-red-500" title="Elimina" data-testid={`delete-client-${c.id}`}><Trash2 size={14} /></button>
                        {expandedRow === c.id ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
                      </div>
                    </td>
                  </tr>
                  {expandedRow === c.id && (
                    <tr>
                      <td colSpan={7} className="bg-zinc-50 px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest font-heading mb-1">Sede Legale</p>
                            <p className="text-zinc-700">{c.indirizzo || "-"}</p>
                            <p className="text-zinc-700">{[c.cap, c.citta, c.provincia ? `(${c.provincia})` : ""].filter(Boolean).join(" ") || "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest font-heading mb-1">Dati Fiscali</p>
                            <p className="text-zinc-700">P.IVA: {c.partita_iva || "-"}</p>
                            <p className="text-zinc-700">C.F.: {c.codice_fiscale || "-"}</p>
                            <p className="text-zinc-700">SDI: {c.codice_sdi || "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest font-heading mb-1">Contatti</p>
                            <p className="text-zinc-700">Tel: {c.telefono_azienda || "-"}</p>
                            <p className="text-zinc-700">PEC: {c.pec || "-"}</p>
                          </div>
                          <div>
                            <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest font-heading mb-1">Referente</p>
                            <p className="text-zinc-700">{c.referente_nome || "-"}</p>
                            <p className="text-zinc-700">{c.referente_ruolo || ""}</p>
                            <p className="text-zinc-700">{c.referente_telefono || "-"}</p>
                          </div>
                          {c.note && (
                            <div className="col-span-2 md:col-span-4">
                              <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest font-heading mb-1">Note</p>
                              <p className="text-zinc-700">{c.note}</p>
                            </div>
                          )}
                          {c.warehouse_enabled && (
                            <div className="col-span-2 md:col-span-4">
                              <StorageContractCard email={c.email} />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 overflow-auto" data-testid="client-form-modal">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto mb-6">
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-heading text-lg font-bold uppercase">{editId ? "Modifica Cliente" : "Nuovo Cliente"}</h3>
              <button onClick={() => { setShowForm(false); setForm({ ...emptyForm }); }} className="p-2 text-zinc-400 hover:text-zinc-600"><X size={20} /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">

              {/* Accesso */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><User size={12} /> Accesso Area Clienti</legend>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <FormField form={form} setForm={setForm} label="Nome / Ragione Sociale *" field="name" placeholder="Mario Rossi / Rossi SRL" half />
                  <FormField form={form} setForm={setForm} label="Email *" field="email" type="email" placeholder="email@azienda.com" half />
                  <FormField form={form} setForm={setForm} label={editId ? "Nuova Password (lascia vuoto per non cambiare)" : "Password *"} field="password" placeholder={editId ? "••••••" : "Password iniziale"} half />
                  <FormField form={form} setForm={setForm} label="Telefono Azienda" field="telefono_azienda" placeholder="+39 0376 xxxxxx" half />
                </div>
              </fieldset>

              {/* Dati Aziendali / Fatturazione */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><Building2 size={12} /> Dati Aziendali / Fatturazione</legend>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <FormField form={form} setForm={setForm} label="Ragione Sociale" field="company" placeholder="Rossi SRL" half />
                  <FormField form={form} setForm={setForm} label="Partita IVA" field="partita_iva" placeholder="IT01234567890" half />
                  <FormField form={form} setForm={setForm} label="Codice Fiscale" field="codice_fiscale" placeholder="RSSMRA80A01H501Z" half />
                  <FormField form={form} setForm={setForm} label="Codice SDI (e-fattura)" field="codice_sdi" placeholder="A1B2C3D" half />
                  <FormField form={form} setForm={setForm} label="PEC" field="pec" type="email" placeholder="azienda@pec.it" half />
                </div>
              </fieldset>

              {/* Sede Legale */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><MapPin size={12} /> Sede Legale</legend>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <FormField form={form} setForm={setForm} label="Indirizzo" field="indirizzo" placeholder="Via Roma 1" />
                  <FormField form={form} setForm={setForm} label="Citta" field="citta" placeholder="Milano" half />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField form={form} setForm={setForm} label="CAP" field="cap" placeholder="20100" half />
                    <FormField form={form} setForm={setForm} label="Provincia" field="provincia" placeholder="MI" half />
                  </div>
                </div>
              </fieldset>

              {/* Referente */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><Phone size={12} /> Persona di Riferimento</legend>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <FormField form={form} setForm={setForm} label="Nome e Cognome" field="referente_nome" placeholder="es. Marco Bianchi" half />
                  <FormField form={form} setForm={setForm} label="Ruolo" field="referente_ruolo" placeholder="es. Responsabile Logistica" half />
                  <FormField form={form} setForm={setForm} label="Telefono Diretto" field="referente_telefono" placeholder="+39 333 xxxxxxx" half />
                </div>
              </fieldset>

              {/* Note */}
              <div>
                <label className="block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading">Note</label>
                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none h-20 resize-none"
                  placeholder="Note interne sul cliente..." data-testid="client-note" />
              </div>

              {/* Servizio Magazzino */}
              <fieldset className="border border-zinc-200 rounded-sm p-4">
                <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><Warehouse size={12} /> Servizio Magazzino</legend>
                <label className="flex items-center gap-3 cursor-pointer mt-1" data-testid="warehouse-toggle">
                  <div className={`relative w-11 h-6 rounded-full transition-colors ${form.warehouse_enabled ? "bg-[#FF5200]" : "bg-zinc-300"}`}
                    onClick={() => setForm({ ...form, warehouse_enabled: !form.warehouse_enabled })}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.warehouse_enabled ? "translate-x-5" : ""}`} />
                  </div>
                  <span className="text-sm font-semibold">{form.warehouse_enabled ? "Attivo" : "Disattivato"}</span>
                  <span className="text-xs text-zinc-400">— Abilita logistica e gestione magazzino per questo cliente</span>
                </label>
              </fieldset>

              {/* Accessi aggiuntivi — solo in modalita modifica */}
              {editId && <ClientSubUsers clientId={editId} />}

              <button type="submit" disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5200] text-white h-11 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm disabled:opacity-50"
                data-testid="client-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                {editId ? "Aggiorna Cliente" : "Crea Cliente"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ClientSubUsers({ clientId }) {
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [showAdd, setShowAdd] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [newUser, setNewUser] = React.useState({ name: "", email: "", password: "" });
  const [pwdResetFor, setPwdResetFor] = React.useState(null);
  const [pwdValue, setPwdValue] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/clients/${clientId}/users`, authHeaders());
      setUsers(r.data || []);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, [clientId]);

  React.useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || newUser.password.length < 6) {
      toast.error("Nome, email e password (min 6 caratteri) obbligatori");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/clients/${clientId}/users`, newUser, authHeaders());
      toast.success("Accesso aggiuntivo creato");
      setNewUser({ name: "", email: "", password: "" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione accesso");
    } finally { setAdding(false); }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Eliminare l'accesso ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}/users/${encodeURIComponent(email)}`, authHeaders());
      toast.success("Accesso eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}/users/${encodeURIComponent(u.email)}`, { active: !u.active }, authHeaders());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const handlePasswordReset = async (email) => {
    if (pwdValue.length < 6) { toast.error("La password deve essere di almeno 6 caratteri"); return; }
    try {
      await axios.put(`${API}/admin/clients/${clientId}/users/${encodeURIComponent(email)}`, { password: pwdValue }, authHeaders());
      toast.success("Password aggiornata");
      setPwdResetFor(null);
      setPwdValue("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento password");
    }
  };

  return (
    <fieldset className="border border-zinc-200 rounded-sm p-4" data-testid="client-subusers-section">
      <legend className="text-xs font-bold tracking-widest uppercase text-zinc-500 px-2 font-heading flex items-center gap-1"><UserPlus size={12} /> Accessi Aggiuntivi (Multi-utente)</legend>
      <p className="text-[11px] text-zinc-500 mb-3 mt-1">Aggiungi altre persone che possono accedere all'area cliente con email e password proprie. Tutti vedono gli stessi dati (rubrica, spedizioni, ritiri). Le email di conferma vengono inviate solo a chi ha creato l'ordine.</p>

      {loading ? (
        <p className="text-xs text-zinc-400">Caricamento...</p>
      ) : (
        <>
          {users.length === 0 && !showAdd && (
            <p className="text-xs text-zinc-400 mb-3">Nessun accesso aggiuntivo. Solo l'email principale del cliente puo accedere.</p>
          )}
          {users.length > 0 && (
            <div className="border border-zinc-200 rounded divide-y divide-zinc-100 mb-3">
              {users.map((u) => (
                <div key={u.email} className="px-3 py-2 flex flex-col gap-2" data-testid={`subuser-row-${u.email}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#09090B]">{u.name}</p>
                      <p className="text-xs text-zinc-500">{u.email} {u.active === false && <span className="text-red-500">· DISATTIVO</span>}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={() => { setPwdResetFor(pwdResetFor === u.email ? null : u.email); setPwdValue(""); }}
                        className="p-1.5 border border-zinc-200 rounded-sm text-zinc-500 hover:text-blue-600 hover:bg-blue-50"
                        title="Cambia password" data-testid={`subuser-pwd-btn-${u.email}`}>
                        <KeyRound size={13} />
                      </button>
                      <button type="button" onClick={() => handleToggleActive(u)}
                        className="p-1.5 border border-zinc-200 rounded-sm text-zinc-500 hover:text-amber-600 hover:bg-amber-50"
                        title={u.active === false ? "Riattiva" : "Disattiva"} data-testid={`subuser-toggle-${u.email}`}>
                        {u.active === false ? <UserCheck size={13} /> : <UserX size={13} />}
                      </button>
                      <button type="button" onClick={() => handleDelete(u.email)}
                        className="p-1.5 border border-red-200 text-red-500 rounded-sm hover:bg-red-50"
                        title="Elimina" data-testid={`subuser-del-${u.email}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {pwdResetFor === u.email && (
                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded p-2">
                      <input type="text" value={pwdValue} onChange={(e) => setPwdValue(e.target.value)}
                        placeholder="Nuova password (min 6 caratteri)"
                        className="flex-1 h-8 px-2 border border-zinc-200 rounded-sm text-xs focus:border-[#FF5200] outline-none"
                        data-testid={`subuser-pwd-input-${u.email}`} />
                      <button type="button" onClick={() => handlePasswordReset(u.email)}
                        className="h-8 px-3 bg-blue-600 text-white text-[11px] font-bold uppercase rounded-sm hover:bg-blue-700"
                        data-testid={`subuser-pwd-save-${u.email}`}>Salva</button>
                      <button type="button" onClick={() => { setPwdResetFor(null); setPwdValue(""); }}
                        className="text-zinc-500 hover:text-zinc-700"><X size={14} /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {showAdd ? (
            <div className="bg-zinc-50 border border-zinc-200 rounded p-3 space-y-2" data-testid="subuser-add-form">
              <input type="text" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Nome completo *" className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                data-testid="subuser-add-name" />
              <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="Email login *" className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                data-testid="subuser-add-email" />
              <input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Password (min 6 caratteri) *" className="w-full h-9 px-2 border border-zinc-200 rounded-sm text-sm focus:border-[#FF5200] outline-none"
                data-testid="subuser-add-password" />
              <div className="flex items-center gap-2 justify-end">
                <button type="button" onClick={() => { setShowAdd(false); setNewUser({ name: "", email: "", password: "" }); }}
                  className="text-xs font-bold uppercase text-zinc-600 hover:text-zinc-800 px-3 py-1.5">Annulla</button>
                <button type="button" onClick={handleAdd} disabled={adding}
                  className="h-9 px-4 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00] disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="subuser-add-submit">
                  {adding ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />} Crea accesso
                </button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-1.5 h-9 px-3 border border-dashed border-zinc-300 text-zinc-600 rounded-sm hover:border-[#FF5200] hover:text-[#FF5200] text-xs font-bold uppercase"
              data-testid="subuser-add-toggle">
              <UserPlus size={13} /> Aggiungi accesso
            </button>
          )}
        </>
      )}
    </fieldset>
  );
}


function StorageContractCard({ email }) {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [priceNotes, setPriceNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({ to: "", subject: "", message: "" });
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [diff, setDiff] = useState(null);
  const [showCustomEditor, setShowCustomEditor] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/clients/${encodeURIComponent(email)}/storage-contract`, authHeaders())
      .then((r) => setC(r.data))
      .catch(() => setC(null));
    // Fetch existing price list notes from the client list endpoint isn't ideal; use a dedicated field.
    // Since GET client details isn't per-client here, we load notes lazily when opening the panel.
  }, [email]);

  const loadNotes = async () => {
    try {
      // Lightweight: fetch all clients and pick this one. If a per-client endpoint is added later, swap here.
      const r = await axios.get(`${API}/admin/clients`, authHeaders());
      const cl = (r.data || []).find((x) => x.email === email);
      setPriceNotes(cl?.price_list_notes || "");
    } catch { /* silent */ }
  };

  if (!c) return null;

  const defaults = c.defaults || {
    storage_cost_per_pallet: 10,
    in_cost_per_pallet: 4,
    out_cost_per_pallet: 4,
    monthly_insurance_flat: 104.16,
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/clients/${encodeURIComponent(email)}/storage-contract`, c, authHeaders());
      if (r.data.auto_snapshot) {
        toast.success("Contratto salvato — snapshot listino archiviato automaticamente");
        if (showHistory) loadHistory();
      } else {
        toast.success("Contratto stoccaggio salvato");
      }
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/admin/clients/${encodeURIComponent(email)}/price-list-notes`,
        { notes: priceNotes }, authHeaders());
      toast.success("Note listino salvate");
    } catch {
      toast.error("Errore nel salvataggio note");
    } finally {
      setSavingNotes(false);
    }
  };

  const downloadPriceList = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(
        `${API}/admin/clients/${encodeURIComponent(email)}/price-list-pdf`,
        { ...authHeaders(), responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Also trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = `listino_prezzi_${email.split("@")[0]}.pdf`;
      a.click();
    } catch {
      toast.error("Errore generazione PDF listino");
    } finally {
      setDownloading(false);
    }
  };

  const openSendModal = () => {
    setSendForm({ to: email, subject: "Listino Prezzi MV Trasporti", message: "" });
    setShowSend(true);
  };

  const sendEmail = async () => {
    if (!sendForm.to) { toast.error("Email destinatario obbligatoria"); return; }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/clients/${encodeURIComponent(email)}/price-list/send`,
        sendForm, authHeaders()
      );
      toast.success(`Listino inviato a ${r.data.sent_to}`);
      setShowSend(false);
      if (showHistory) loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio email fallito");
    } finally {
      setSending(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await axios.get(
        `${API}/admin/clients/${encodeURIComponent(email)}/price-list/history`, authHeaders()
      );
      setHistory(r.data.snapshots || []);
    } catch {
      toast.error("Errore caricamento storico");
    }
  };

  const toggleHistory = async () => {
    if (!showHistory) await loadHistory();
    setShowHistory(!showHistory);
  };

  const saveCurrentSnapshot = async () => {
    try {
      await axios.post(`${API}/admin/clients/${encodeURIComponent(email)}/price-list/save`, {}, authHeaders());
      toast.success("Snapshot salvato");
      if (showHistory) loadHistory();
    } catch {
      toast.error("Errore salvataggio snapshot");
    }
  };

  const downloadSnapshotPdf = async (snap) => {
    try {
      const r = await axios.get(
        `${API}/admin/clients/${encodeURIComponent(email)}/price-list/${snap.id}/pdf`,
        { ...authHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("PDF snapshot non disponibile");
    }
  };

  const openDiff = async (snap) => {
    try {
      const r = await axios.get(
        `${API}/admin/clients/${encodeURIComponent(email)}/price-list/diff?snapshot_id=${snap.id}`,
        authHeaders()
      );
      setDiff({ ...r.data, snapshot: snap });
    } catch {
      toast.error("Errore calcolo differenze");
    }
  };

  const applyDefaults = () => {
    setC({
      ...c,
      storage_cost_per_pallet: defaults.storage_cost_per_pallet,
      in_cost_per_pallet: defaults.in_cost_per_pallet,
      out_cost_per_pallet: defaults.out_cost_per_pallet,
      monthly_insurance_flat: defaults.monthly_insurance_flat,
    });
    toast.success("Tariffe ripristinate ai default globali");
  };

  const rows = [
    { key: "storage_cost_per_pallet", label: "Stoccaggio €/bancale/mese" },
    { key: "in_cost_per_pallet", label: "Handling Ingresso €/bancale" },
    { key: "out_cost_per_pallet", label: "Handling Uscita €/bancale" },
    { key: "monthly_insurance_flat", label: "Assicurazione All Risk €/mese" },
  ];

  const showActivityHint = c.has_warehouse_activity && !c.enabled;

  return (
    <div className="border-t border-zinc-200 pt-4 mt-2" data-testid={`storage-contract-${email}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-zinc-400 font-bold uppercase tracking-widest font-heading flex items-center gap-2">
          <Warehouse size={12} /> Contratto Stoccaggio Magazzino
          {c.has_warehouse_activity && (
            <span
              className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold tracking-wider"
              title={`${c.current_pallets} bancali in giacenza · ${c.movements_count} movimenti totali`}
            >
              Magazzino attivo · {Math.round(c.current_pallets)} bancali
            </span>
          )}
        </p>
        <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer">
          <input
            type="checkbox"
            checked={!!c.enabled}
            onChange={(e) => setC({ ...c, enabled: e.target.checked })}
            className="h-4 w-4 accent-blue-700"
            data-testid={`storage-enabled-${email}`}
          />
          <span className={c.enabled ? "text-blue-700 font-bold" : "text-zinc-500"}>
            {c.enabled ? "Abilitato" : "Disabilitato"}
          </span>
        </label>
      </div>

      {showActivityHint && (
        <div className="mb-3 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900" data-testid={`storage-activity-hint-${email}`}>
          <span className="mt-0.5">⚠️</span>
          <span>
            Questo cliente ha già attività di magazzino ({Math.round(c.current_pallets)} bancali in giacenza,{" "}
            {c.movements_count} movimenti). Abilita il contratto per iniziare a fatturare stoccaggio e handling.
          </span>
        </div>
      )}

      {c.enabled && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {rows.map((r) => (
              <div key={r.key}>
                <label className="text-[10px] text-zinc-500 block mb-1">{r.label}</label>
                <input
                  type="number" step="0.01" min="0"
                  value={r.key === "monthly_insurance_flat" && c.insurance_disabled ? 0 : (c[r.key] ?? "")}
                  disabled={r.key === "monthly_insurance_flat" && !!c.insurance_disabled}
                  placeholder={defaults[r.key] != null ? String(defaults[r.key]) : "0"}
                  onChange={(e) => setC({ ...c, [r.key]: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })}
                  className="w-full h-8 px-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none disabled:bg-zinc-100 disabled:text-zinc-400"
                  data-testid={`storage-${r.key}-${email}`}
                />
                <p className="text-[9px] text-zinc-400 mt-0.5">
                  Default globale: <span className="font-bold text-zinc-500">{(defaults[r.key] ?? 0).toFixed(2)} €</span>
                </p>
              </div>
            ))}
          </div>
          <label className="mt-3 flex items-start gap-2 cursor-pointer rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid={`storage-insurance-disabled-${email}`}>
            <input
              type="checkbox"
              checked={!!c.insurance_disabled}
              onChange={(e) => setC({ ...c, insurance_disabled: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-blue-700"
              data-testid={`storage-insurance-disabled-input-${email}`}
            />
            <span className="text-[11px] text-zinc-700">
              <span className="font-bold">Assicurazione All Risk a zero</span> — nessun forfait mensile per questo cliente: la voce non compare in fattura né nel listino.
            </span>
          </label>
          <p className="text-[10px] text-zinc-400 mt-2">
            I default globali (Impostazioni → Tariffe Stoccaggio) fanno da fallback quando i valori sono a 0.
          </p>
        </>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-blue-800 disabled:opacity-50"
          data-testid={`storage-save-${email}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Salva contratto
        </button>
        {c.enabled && (
          <button
            onClick={applyDefaults}
            className="inline-flex items-center gap-2 border border-zinc-300 bg-white text-zinc-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50"
            data-testid={`storage-apply-defaults-${email}`}
            title="Reimposta le tariffe di questo cliente ai default globali"
          >
            <RefreshCw size={12} /> Ripristina Default
          </button>
        )}
        <button
          onClick={downloadPriceList}
          disabled={downloading}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-orange-600 disabled:opacity-50"
          data-testid={`storage-pricelist-${email}`}
          title="Scarica il listino prezzi PDF da presentare al cliente"
        >
          {downloading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
          Scarica Listino PDF
        </button>
        <button
          onClick={() => setShowCustomEditor(true)}
          className="inline-flex items-center gap-2 bg-purple-600 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-purple-700"
          data-testid={`storage-custom-pl-${email}`}
          title="Crea un listino prezzi personalizzato con voci libere (sostituisce il layout automatico)"
        >
          <ListPlus size={12} /> Listino Custom
        </button>
        <button
          onClick={openSendModal}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-blue-700"
          data-testid={`storage-send-${email}`}
          title="Invia il listino via email al cliente"
        >
          <Mail size={12} /> Invia al Cliente
        </button>
        <button
          onClick={toggleHistory}
          className="inline-flex items-center gap-2 border border-zinc-300 bg-white text-zinc-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50"
          data-testid={`storage-history-toggle-${email}`}
        >
          <History size={12} /> {showHistory ? "Nascondi Storico" : "Storico Listini"}
        </button>
        <button
          onClick={() => { if (!showNotes) loadNotes(); setShowNotes(!showNotes); }}
          className="inline-flex items-center gap-2 border border-zinc-300 bg-white text-zinc-700 px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-zinc-50"
          data-testid={`storage-notes-toggle-${email}`}
        >
          <FileText size={12} /> {showNotes ? "Nascondi Note" : "Note Listino"}
        </button>
      </div>

      {showHistory && (
        <div className="mt-3 border-t border-zinc-100 pt-3" data-testid={`storage-history-block-${email}`}>
          <div className="flex items-center justify-between mb-2">
            <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest">
              Storico Listini ({history.length})
            </label>
            <button
              onClick={saveCurrentSnapshot}
              className="text-[10px] font-bold uppercase text-blue-700 hover:underline"
              data-testid={`storage-snapshot-save-${email}`}
            >
              Salva snapshot corrente
            </button>
          </div>
          {history.length === 0 ? (
            <p className="text-xs text-zinc-400 py-3 text-center">Nessuno snapshot salvato. Salvane uno per iniziare a tracciare le versioni.</p>
          ) : (
            <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
              {history.map((h) => (
                <li key={h.id} className="flex items-center gap-3 px-3 py-2" data-testid={`storage-history-row-${h.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#09090B]">
                      {new Date(h.created_at).toLocaleString("it-IT")}
                      {h.source === "auto" && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-bold tracking-wider uppercase" title="Snapshot creato automaticamente al cambio tariffe">
                          Auto
                        </span>
                      )}
                      {h.sent_to && <span className="ml-2 text-[10px] text-emerald-600 uppercase font-bold">✉ inviato a {h.sent_to}</span>}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      Stoccaggio {h.tariff?.storage_cost_per_pallet?.toFixed(2)}€ · Ingresso {h.tariff?.in_cost_per_pallet?.toFixed(2)}€ · Uscita {h.tariff?.out_cost_per_pallet?.toFixed(2)}€ · Assic. {h.tariff?.monthly_insurance_flat?.toFixed(2)}€
                    </p>
                  </div>
                  <button onClick={() => downloadSnapshotPdf(h)} title="Scarica PDF" className="text-xs font-bold uppercase text-blue-700 hover:underline">PDF</button>
                  <button onClick={() => openDiff(h)} title="Confronta con listino attuale" className="text-xs font-bold uppercase text-purple-700 hover:underline">Diff</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showNotes && (
        <div className="mt-3 border-t border-zinc-100 pt-3" data-testid={`storage-notes-block-${email}`}>
          <label className="text-[10px] text-zinc-500 uppercase font-bold tracking-widest block mb-1">
            Note personalizzate del listino (mostrate in fondo al PDF)
          </label>
          <textarea
            rows={4}
            value={priceNotes}
            onChange={(e) => setPriceNotes(e.target.value)}
            placeholder="Es. Pagamento a 30gg fine mese. Offerta valida 30gg dall'emissione. Servizi extra su preventivo."
            className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm focus:border-blue-600 outline-none"
            data-testid={`storage-notes-input-${email}`}
          />
          <div className="mt-2">
            <button
              onClick={saveNotes}
              disabled={savingNotes}
              className="inline-flex items-center gap-2 bg-blue-700 text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-sm hover:bg-blue-800 disabled:opacity-50"
              data-testid={`storage-notes-save-${email}`}
            >
              {savingNotes ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Salva Note
            </button>
          </div>
        </div>
      )}

      {showSend && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="pricelist-send-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSend(false); }}
        >
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#09090B]">Invia Listino al Cliente</h3>
                <p className="text-xs text-zinc-500">Verrà inviato un PDF via Resend e archiviato come snapshot</p>
              </div>
              <button onClick={() => setShowSend(false)} className="text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Email destinatario *</label>
                <input type="email" value={sendForm.to} onChange={(e) => setSendForm({...sendForm, to: e.target.value})}
                  className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm" data-testid="pricelist-send-to" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Oggetto</label>
                <input type="text" value={sendForm.subject} onChange={(e) => setSendForm({...sendForm, subject: e.target.value})}
                  className="w-full h-9 px-3 border border-zinc-300 rounded-sm text-sm" data-testid="pricelist-send-subject" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 block mb-1">Messaggio</label>
                <textarea rows={4} value={sendForm.message} onChange={(e) => setSendForm({...sendForm, message: e.target.value})}
                  placeholder="Testo introduttivo. Se vuoto verrà usato il testo di default."
                  className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" data-testid="pricelist-send-message" />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
              <button onClick={() => setShowSend(false)} className="border border-zinc-300 bg-white text-zinc-700 px-4 py-2 text-xs font-bold uppercase rounded-sm">Annulla</button>
              <button onClick={sendEmail} disabled={sending}
                className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 py-2 text-xs font-bold uppercase rounded-sm hover:bg-blue-800 disabled:opacity-50"
                data-testid="pricelist-send-confirm">
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                Invia Ora
              </button>
            </div>
          </div>
        </div>
      )}

      {diff && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="pricelist-diff-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setDiff(null); }}
        >
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#09090B]">Confronto Tariffe</h3>
                <p className="text-xs text-zinc-500">
                  Snapshot del {new Date(diff.snapshot.created_at).toLocaleString("it-IT")}
                  {" · vs listino corrente"}
                </p>
              </div>
              <button onClick={() => setDiff(null)} className="text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-auto">
              {!diff.any_changes && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm px-3 py-2 text-sm">
                  Nessuna differenza tra questo snapshot e il listino corrente.
                </div>
              )}
              <DiffTable title="Contratto Stoccaggio" rows={diff.tariff_diff} />
              <DiffTable title="Servizi Extra Spedizione" rows={diff.extras_diff} />
            </div>
          </div>
        </div>
      )}
      {showCustomEditor && (
        <CustomPriceListModal
          email={email}
          clientName={email}
          onClose={() => setShowCustomEditor(false)}
          onSaved={() => { if (showHistory) loadHistory(); }}
        />
      )}
    </div>
  );
}

function DiffTable({ title, rows }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 mb-1">{title}</p>
      <table className="w-full text-sm border border-zinc-200 rounded-sm overflow-hidden">
        <thead className="bg-zinc-50">
          <tr>
            <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Voce</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Precedente</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Attuale</th>
            <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Δ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const changed = r.delta !== 0;
            const positive = r.delta > 0;
            return (
              <tr key={r.key} className={`border-t border-zinc-100 ${changed ? "" : "text-zinc-400"}`}>
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 text-right font-mono">{r.prev.toFixed(2)} €</td>
                <td className={`px-3 py-2 text-right font-mono ${changed ? "font-bold text-[#09090B]" : ""}`}>{r.curr.toFixed(2)} €</td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${changed ? (positive ? "text-emerald-600" : "text-red-600") : ""}`}>
                  {r.delta === 0 ? "—" : `${positive ? "+" : ""}${r.delta.toFixed(2)} €${r.pct != null ? ` (${positive ? "+" : ""}${r.pct.toFixed(1)}%)` : ""}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
